import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import {
  Users, Search, Unlock, Lock, KeyRound, ShieldAlert, RefreshCw,
  Loader2, CheckCircle, XCircle, Server, ChevronDown, ChevronUp, UserCog
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function UserRow({ adUser, serverId, onRefresh }) {
  const [acting, setActing] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [newPwd, setNewPwd] = useState("");

  const unlockUser = async () => {
    setActing(true);
    try {
      const res = await axios.post(`${API}/ad/unlock-user`, { server_id: serverId, username: adUser.SamAccountName });
      if (res.data.success) { toast.success(res.data.message); onRefresh(); }
      else toast.error(res.data.message);
    } catch { toast.error("Failed to unlock user"); }
    finally { setActing(false); }
  };

  const disableUser = async () => {
    if (!window.confirm(`Disable account "${adUser.SamAccountName}"?`)) return;
    setActing(true);
    try {
      const res = await axios.post(`${API}/ad/disable-user`, { server_id: serverId, username: adUser.SamAccountName });
      if (res.data.success) { toast.success(res.data.message); onRefresh(); }
      else toast.error(res.data.message);
    } catch { toast.error("Failed to disable user"); }
    finally { setActing(false); }
  };

  const enableUser = async () => {
    setActing(true);
    try {
      const res = await axios.post(`${API}/ad/enable-user`, { server_id: serverId, username: adUser.SamAccountName });
      if (res.data.success) { toast.success(res.data.message); onRefresh(); }
      else toast.error(res.data.message);
    } catch { toast.error("Failed to enable user"); }
    finally { setActing(false); }
  };

  const resetPassword = async () => {
    if (!newPwd || newPwd.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setActing(true);
    try {
      const res = await axios.post(`${API}/ad/reset-password`, {
        server_id: serverId, username: adUser.SamAccountName, new_password: newPwd
      });
      if (res.data.success) { toast.success(res.data.message); setResetOpen(false); setNewPwd(""); }
      else toast.error(res.data.message);
    } catch { toast.error("Failed to reset password"); }
    finally { setActing(false); }
  };

  return (
    <>
      <div className="flex items-center gap-3 p-3 bg-[#0c0c0e] border border-white/5 hover:border-white/10 rounded-lg transition-all"
        data-testid={`ad-user-row-${adUser.SamAccountName}`}>
        <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
          <UserCog className="w-5 h-5 text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white truncate">{adUser.Name || adUser.SamAccountName}</span>
            <span className="text-xs font-mono text-zinc-500">{adUser.SamAccountName}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {adUser.Department && <span className="text-xs text-zinc-500">{adUser.Department}</span>}
            {adUser.Title && <span className="text-xs text-zinc-600">- {adUser.Title}</span>}
            {adUser.EmailAddress && <span className="text-xs text-zinc-600 ml-1">{adUser.EmailAddress}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {adUser.LockedOut && (
            <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-[10px]">
              <Lock className="w-3 h-3 mr-1" /> Locked
            </Badge>
          )}
          <Badge className={`text-[10px] border ${adUser.Enabled !== false ? "bg-green-500/10 text-green-400 border-green-500/20" : "bg-zinc-500/10 text-zinc-400 border-zinc-500/20"}`}>
            {adUser.Enabled !== false ? "Active" : "Disabled"}
          </Badge>
        </div>
        <div className="flex gap-1.5 shrink-0">
          {adUser.LockedOut && (
            <Button size="sm" variant="outline" disabled={acting}
              className="bg-green-500/10 border-green-500/20 hover:bg-green-500/20 text-green-400 h-7 text-xs"
              onClick={unlockUser} data-testid={`unlock-btn-${adUser.SamAccountName}`}>
              {acting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlock className="w-3 h-3 mr-1" />}
              Unlock
            </Button>
          )}
          <Button size="sm" variant="outline" disabled={acting}
            className="bg-yellow-500/10 border-yellow-500/20 hover:bg-yellow-500/20 text-yellow-400 h-7 text-xs"
            onClick={() => setResetOpen(true)} data-testid={`reset-pwd-btn-${adUser.SamAccountName}`}>
            <KeyRound className="w-3 h-3 mr-1" /> Reset Pwd
          </Button>
          {adUser.Enabled !== false ? (
            <Button size="sm" variant="outline" disabled={acting}
              className="bg-red-500/10 border-red-500/20 hover:bg-red-500/20 text-red-400 h-7 text-xs"
              onClick={disableUser} data-testid={`disable-btn-${adUser.SamAccountName}`}>
              <Lock className="w-3 h-3 mr-1" /> Disable
            </Button>
          ) : (
            <Button size="sm" variant="outline" disabled={acting}
              className="bg-blue-500/10 border-blue-500/20 hover:bg-blue-500/20 text-blue-400 h-7 text-xs"
              onClick={enableUser} data-testid={`enable-btn-${adUser.SamAccountName}`}>
              <CheckCircle className="w-3 h-3 mr-1" /> Enable
            </Button>
          )}
        </div>
      </div>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="bg-[#0c0c0e] border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-mono flex items-center gap-2 text-base">
              <KeyRound className="w-4 h-4 text-yellow-400" /> Reset Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-zinc-400">
              Reset password for <span className="text-white font-medium">{adUser.SamAccountName}</span>
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">New Password</Label>
              <Input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)}
                placeholder="Minimum 8 characters"
                className="bg-black/50 border-white/10 text-white font-mono"
                data-testid="reset-password-input" />
            </div>
            <p className="text-[10px] text-zinc-500">User will be required to change password at next logon.</p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 border-white/10 text-white h-8 text-xs"
                onClick={() => setResetOpen(false)}>Cancel</Button>
              <Button className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white h-8 text-xs"
                onClick={resetPassword} disabled={acting || !newPwd}
                data-testid="confirm-reset-pwd-btn">
                {acting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <KeyRound className="w-3 h-3 mr-1" />}
                Reset Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function ADManagement() {
  const { user } = useAuth();
  const [servers, setServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [lockedUsers, setLockedUsers] = useState([]);
  const [lockedLoading, setLockedLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("locked");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/servers`);
        const winServers = res.data.filter(s => s.os_type === "windows" && s.winrm_configured);
        setServers(winServers);
        if (winServers.length > 0) setSelectedServer(winServers[0].id);
      } catch { toast.error("Failed to load servers"); }
    })();
  }, []);

  const fetchLockedUsers = useCallback(async () => {
    if (!selectedServer) return;
    setLockedLoading(true);
    try {
      const res = await axios.post(`${API}/ad/locked-users?server_id=${selectedServer}`);
      if (res.data.success) setLockedUsers(res.data.users || []);
      else toast.error(res.data.error || "Failed to fetch locked users");
    } catch { toast.error("Failed to fetch locked users"); }
    finally { setLockedLoading(false); }
  }, [selectedServer]);

  useEffect(() => {
    if (selectedServer) fetchLockedUsers();
  }, [selectedServer, fetchLockedUsers]);

  const searchUsers = async () => {
    if (!selectedServer || !searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await axios.post(`${API}/ad/search-users`, {
        server_id: selectedServer, query: searchQuery.trim()
      });
      if (res.data.success) setSearchResults(res.data.users || []);
      else toast.error(res.data.error || "Search failed");
    } catch { toast.error("Search failed"); }
    finally { setSearching(false); }
  };

  const currentServer = servers.find(s => s.id === selectedServer);

  return (
    <div className="p-6 space-y-6" data-testid="ad-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-mono text-white tracking-tight flex items-center gap-3">
            <ShieldAlert className="w-8 h-8 text-cyan-400" /> Active Directory
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Manage Windows AD users — unlock accounts, reset passwords, enable/disable
          </p>
        </div>
      </div>

      {/* Server Selector */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <Server className="w-4 h-4 text-zinc-500" />
          <Label className="text-xs text-zinc-400">Domain Controller</Label>
        </div>
        <Select value={selectedServer} onValueChange={setSelectedServer}>
          <SelectTrigger className="w-72 bg-black/50 border-white/10 text-white text-sm h-9"
            data-testid="ad-server-select">
            <SelectValue placeholder="Select a Windows server..." />
          </SelectTrigger>
          <SelectContent className="bg-[#0c0c0e] border-white/10">
            {servers.length === 0 ? (
              <SelectItem value="none" disabled>No Windows servers with WinRM configured</SelectItem>
            ) : (
              servers.map(s => (
                <SelectItem key={s.id} value={s.id}>
                  <span className="text-sm">{s.name} ({s.ip_address})</span>
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
        {currentServer && (
          <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-xs">
            {currentServer.status}
          </Badge>
        )}
      </div>

      {servers.length === 0 ? (
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardContent className="py-16 text-center space-y-3">
            <ShieldAlert className="w-14 h-14 text-zinc-700 mx-auto" />
            <p className="text-lg font-medium text-white">No Windows servers with WinRM</p>
            <p className="text-zinc-500 text-sm max-w-md mx-auto">
              Add a Windows server and configure WinRM credentials in the Servers page to manage Active Directory users.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Tab Buttons */}
          <div className="flex gap-1 p-1 bg-black/30 border border-white/5 rounded-lg w-fit">
            <button onClick={() => setActiveTab("locked")}
              className={`px-4 py-2 rounded-md text-xs font-medium transition-all ${
                activeTab === "locked" ? "bg-red-500/20 text-red-400" : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`} data-testid="tab-locked-users">
              <Lock className="w-3.5 h-3.5 inline mr-1.5" /> Locked Users
            </button>
            <button onClick={() => setActiveTab("search")}
              className={`px-4 py-2 rounded-md text-xs font-medium transition-all ${
                activeTab === "search" ? "bg-blue-500/20 text-blue-400" : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`} data-testid="tab-search-users">
              <Search className="w-3.5 h-3.5 inline mr-1.5" /> Search Users
            </button>
          </div>

          {/* Locked Users Tab */}
          {activeTab === "locked" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-zinc-400 flex items-center gap-2">
                  <Lock className="w-4 h-4 text-red-400" />
                  {lockedUsers.length} locked account{lockedUsers.length !== 1 && "s"}
                </p>
                <Button size="sm" variant="outline" onClick={fetchLockedUsers} disabled={lockedLoading}
                  className="bg-white/5 border-white/10 hover:bg-white/10 text-white text-xs h-7"
                  data-testid="refresh-locked-btn">
                  {lockedLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                  Refresh
                </Button>
              </div>

              {lockedLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-red-400" />
                </div>
              ) : lockedUsers.length === 0 ? (
                <Card className="bg-[#0c0c0e] border-white/5">
                  <CardContent className="py-10 text-center space-y-2">
                    <CheckCircle className="w-10 h-10 text-green-500 mx-auto" />
                    <p className="text-sm text-white font-medium">No locked accounts</p>
                    <p className="text-xs text-zinc-500">All AD user accounts are currently accessible.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {lockedUsers.map((u, i) => (
                    <UserRow key={u.SamAccountName || i} adUser={u} serverId={selectedServer} onRefresh={fetchLockedUsers} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Search Users Tab */}
          {activeTab === "search" && (
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search by name or username..."
                  className="flex-1 bg-black/50 border-white/10 text-white font-mono h-9"
                  onKeyDown={e => e.key === "Enter" && searchUsers()}
                  data-testid="ad-search-input" />
                <Button onClick={searchUsers} disabled={searching || !searchQuery.trim()}
                  className="bg-blue-600 hover:bg-blue-700 text-white h-9"
                  data-testid="ad-search-btn">
                  {searching ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Search className="w-4 h-4 mr-1" />}
                  Search
                </Button>
              </div>

              {searching ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
                </div>
              ) : searchResults.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs text-zinc-500">{searchResults.length} result{searchResults.length !== 1 && "s"}</p>
                  {searchResults.map((u, i) => (
                    <UserRow key={u.SamAccountName || i} adUser={u} serverId={selectedServer} onRefresh={() => searchUsers()} />
                  ))}
                </div>
              ) : searchQuery && !searching ? (
                <Card className="bg-[#0c0c0e] border-white/5">
                  <CardContent className="py-10 text-center space-y-2">
                    <Search className="w-10 h-10 text-zinc-700 mx-auto" />
                    <p className="text-sm text-white">No results</p>
                    <p className="text-xs text-zinc-500">Try a different search term.</p>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-[#0c0c0e] border-white/5">
                  <CardContent className="py-10 text-center space-y-2">
                    <Users className="w-10 h-10 text-zinc-700 mx-auto" />
                    <p className="text-sm text-white">Search Active Directory</p>
                    <p className="text-xs text-zinc-500">Enter a name or username to find AD users.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
